// src/components/CrashCourse/FinalExam/CrashCourseFinalExamFlow.jsx
//
// Drop-in flow covering everything after "all modules complete":
//   FinalExamGateway -> FinalExam -> CertificationResults -> EmailConfirmation -> CertificateOfCompletion
//
// Usage from CrashCourseView.jsx — render this instead of returning to
// CourseHub once the last module's onNextModule fires. See README.md for
// the exact integration diff.

import React, { useState } from "react";
import FinalExamGateway from "./FinalExamGateway";
import FinalExam from "./FinalExam";
import CertificationResults from "./CertificationResults";
import EmailConfirmation from "./EmailConfirmation";
import CertificateOfCompletion from "./CertificateOfCompletion";
import { FINAL_EXAM_BANK } from "./finalExamData";

const SCREENS = { GATEWAY: "gateway", EXAM: "exam", RESULTS: "results", EMAIL: "email", CERT: "cert" };

export default function CrashCourseFinalExamFlow({
  courseId = "WA_PEVO_PREP",     // matches quizzes.ExamQuestion.course_id
  courseTitle,                    // e.g. "Washington Crash Course"
  moduleTitles = [],               // for the gateway checklist
  passThreshold = 80,
  student,                         // { name, email }
  questions,                        // optional override — otherwise pulled from FINAL_EXAM_BANK[courseId]
  onSaveAttempt,                     // optional async (attempt) => {} — POST to /api/quizzes/attempts/
  onCertified,                        // optional (credentialId, issueDate) => {} — persist badge to the user's profile
  onBack,                              // back to CourseHub
  onResendEmail,                        // optional () => {} — POST to /api/courses/certificates/resend/
  initialScreen = SCREENS.GATEWAY,       // pass SCREENS.CERT to jump straight to an existing certificate
  existingCertification = null,           // { credentialId, issueDate } — required when initialScreen is CERT
  isDark,
}) {
  const [screen, setScreen]   = useState(initialScreen);
  const [attempt, setAttempt] = useState(null);
  const [certified, setCertified] = useState(!!existingCertification);
  const [credentialId, setCredentialId] = useState(existingCertification?.credentialId || null);
  const [issueDate, setIssueDate] = useState(existingCertification?.issueDate ? new Date(existingCertification.issueDate) : null);

  const bank = questions || FINAL_EXAM_BANK[courseId] || [];

  if (bank.length === 0 && screen !== SCREENS.CERT) {
    return (
      <div style={{ maxWidth: 560, margin: "60px auto", textAlign: "center", color: isDark ? "#f0f4f8" : "#0d1f3c" }}>
        No final exam questions are configured for course "{courseId}". Add a bank in finalExamData.js or pass a
        `questions` prop.
      </div>
    );
  }

  const handleExamComplete = (result) => {
    setAttempt(result);
    onSaveAttempt?.(result);
    if (result.pass && !certified) {
      const id = `PPCA-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 89999)}`;
      const date = new Date();
      setCertified(true);
      setCredentialId(id);
      setIssueDate(date);
      onCertified?.(id, date);
    }
    setScreen(SCREENS.RESULTS);
  };

  return (
    <div style={{ minHeight: "60vh", background: isDark ? "#0d1f3c" : "#f4f7fb", transition: "background .3s" }}>
      {screen === SCREENS.GATEWAY && (
        <FinalExamGateway
          courseTitle={courseTitle}
          moduleTitles={moduleTitles}
          questionCount={bank.length}
          passThreshold={passThreshold}
          studentEmail={student.email}
          onStart={() => setScreen(SCREENS.EXAM)}
          onReview={onBack}
          isDark={isDark}
        />
      )}

      {screen === SCREENS.EXAM && (
        <FinalExam
          questions={bank}
          passThreshold={passThreshold}
          onComplete={handleExamComplete}
          isDark={isDark}
        />
      )}

      {screen === SCREENS.RESULTS && attempt && (
        <CertificationResults
          attempt={attempt}
          studentEmail={student.email}
          onRetake={() => setScreen(SCREENS.EXAM)}
          onReviewModules={onBack}
          onContinue={() => setScreen(SCREENS.EMAIL)}
          isDark={isDark}
        />
      )}

      {screen === SCREENS.EMAIL && (
        <EmailConfirmation
          studentEmail={student.email}
          credentialId={credentialId}
          courseTitle={courseTitle}
          onResend={onResendEmail}
          onContinue={() => setScreen(SCREENS.CERT)}
          isDark={isDark}
        />
      )}

      {screen === SCREENS.CERT && (
        <CertificateOfCompletion
          studentName={student.name}
          courseTitle={courseTitle}
          credentialId={credentialId}
          issueDate={issueDate}
          onBack={onBack}
          isDark={isDark}
        />
      )}
    </div>
  );
}
